import { useRef, type FC } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import ArticleCard from "./articles/ArticleCard";
import { useScrollReveal } from "../hooks/useScrollReveal";
import { useDocumentMeta } from "../hooks/useDocumentMeta";
import {
  ARTICLE_CATEGORIES,
  getAllArticles,
  type ArticleCategory,
} from "../lib/articles";
import { OG_IMAGE_URL, articlesUrl } from "../lib/articleSeo";

const chipClass = (active: boolean) =>
  `rounded-full border px-4 py-1.5 text-sm font-medium transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400 ${
    active
      ? "border-cyan-400 bg-cyan-400 text-slate-950"
      : "border-slate-600 text-slate-200 hover:border-cyan-400 hover:text-cyan-300"
  }`;

const Articles: FC = () => {
  const ref = useRef<HTMLElement>(null);
  const navigate = useNavigate();
  // strict: false because the route id depends on the parent layout ("/_classic/articles")
  const { category } = useSearch({ strict: false }) as {
    category?: ArticleCategory;
  };
  useScrollReveal(ref);

  useDocumentMeta({
    title: "Articles | Don Benary Lagadan",
    description:
      "In-depth best-practices articles on React, Go, PostgreSQL, AWS and Docker, written from experience on client projects.",
    url: articlesUrl(),
    image: OG_IMAGE_URL,
  });

  const all = getAllArticles();
  const visible = category ? all.filter((a) => a.category === category) : all;

  const select = (next?: (typeof ARTICLE_CATEGORIES)[number]) =>
    navigate({ to: "/articles", search: { category: next } });

  return (
    <section
      ref={ref}
      className="mt-10 min-h-screen rounded-xl bg-[#0a0a0a] pb-12 pt-14 shadow-lg"
    >
      <div className="container mx-auto px-6 md:px-10">
        <header className="mb-10 text-center" data-reveal>
          <h1 className="mb-4 text-4xl font-bold text-white md:text-6xl">
            Articles
          </h1>
          <p className="mx-auto max-w-2xl text-lg text-gray-400">
            Best practices for the stack I ship with, written from client
            projects.
          </p>
        </header>

        <div
          className="mb-10 flex flex-wrap justify-center gap-3"
          role="group"
          aria-label="Filter by category"
          data-reveal
        >
          <button
            type="button"
            aria-pressed={!category}
            className={chipClass(!category)}
            onClick={() => select(undefined)}
          >
            All
          </button>
          {ARTICLE_CATEGORIES.map((c) => (
            <button
              key={c}
              type="button"
              aria-pressed={category === c}
              className={chipClass(category === c)}
              onClick={() => select(c)}
            >
              {c}
            </button>
          ))}
        </div>

        {visible.length === 0 ? (
          <p className="text-center text-gray-400">
            No articles in this category yet.
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
            {visible.map((article) => (
              <ArticleCard key={article.slug} article={article} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default Articles;
